import React, { createContext, useContext, useState, useRef, ReactNode, useCallback, useMemo } from "react";

type EntitySelections = Record<string, Record<string, any>>;

type RowSelectionContextType = {
  selections: EntitySelections;
  setEntitySelection: (entityName: string, selectedRows: Record<string, any>) => void;
  getEntitySelection: (entityName: string) => Record<string, any>;
  clearEntitySelection: (entityName: string) => void;
  clearAllSelections: () => void;
  registerRefresh: (entityKey: string, callback: () => void) => void;
  unregisterRefresh: (entityKey: string, callback: () => void) => void;
  triggerRefresh: (entityKey: string) => void;
};

const RowSelectionContext = createContext<RowSelectionContextType | undefined>(undefined);

export function RowSelectionProvider({ children }: { children: ReactNode }) {
  const [selections, setSelections] = useState<EntitySelections>({});
  const selectionsRef = useRef<EntitySelections>(selections);
  selectionsRef.current = selections;
  const refreshCallbacksRef = useRef<Record<string, Set<() => void>>>({});

  const setEntitySelection = useCallback((entityName: string, selectedRows: Record<string, any>) => {
    setSelections((prev) => {
      // Check if selection actually changed to prevent unnecessary updates
      const currentSelection = prev[entityName];
      const currentKeys = currentSelection ? Object.keys(currentSelection) : [];
      const newKeys = Object.keys(selectedRows);

      // Quick check: same number of keys and same keys
      if (currentKeys.length === newKeys.length && currentKeys.every((key) => newKeys.includes(key))) {
        return prev; // No change, return same reference
      }

      return {
        ...prev,
        [entityName]: selectedRows,
      };
    });
  }, []);

  const getEntitySelection = useCallback((entityName: string): Record<string, any> => {
    return selectionsRef.current[entityName] || {};
  }, []);

  const clearEntitySelection = useCallback((entityName: string) => {
    setSelections((prev) => {
      const newSelections = { ...prev };
      delete newSelections[entityName];
      return newSelections;
    });
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelections({});
  }, []);

  const registerRefresh = useCallback((entityKey: string, callback: () => void) => {
    if (!refreshCallbacksRef.current[entityKey]) {
      refreshCallbacksRef.current[entityKey] = new Set();
    }
    refreshCallbacksRef.current[entityKey].add(callback);
  }, []);

  const unregisterRefresh = useCallback((entityKey: string, callback: () => void) => {
    if (refreshCallbacksRef.current[entityKey]) {
      refreshCallbacksRef.current[entityKey].delete(callback);
      if (refreshCallbacksRef.current[entityKey].size === 0) {
        delete refreshCallbacksRef.current[entityKey];
      }
    }
  }, []);

  const triggerRefresh = useCallback((entityKey: string) => {
    if (refreshCallbacksRef.current[entityKey]) {
      refreshCallbacksRef.current[entityKey].forEach((callback) => callback());
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      selections,
      setEntitySelection,
      getEntitySelection,
      clearEntitySelection,
      clearAllSelections,
      registerRefresh,
      unregisterRefresh,
      triggerRefresh,
    }),
    [
      selections,
      setEntitySelection,
      getEntitySelection,
      clearEntitySelection,
      clearAllSelections,
      registerRefresh,
      unregisterRefresh,
      triggerRefresh,
    ],
  );

  return <RowSelectionContext.Provider value={contextValue}>{children}</RowSelectionContext.Provider>;
}

export function useRowSelectionContext() {
  const context = useContext(RowSelectionContext);
  if (context === undefined) {
    throw new Error("useRowSelectionContext must be used within a RowSelectionProvider");
  }
  return context;
}
